import { FormsEntities } from '@bimser/common';
import * as LayoutCreatorEntities from '@bimser/components/dist/BCLayoutCreator/common/entities';
import * as React from 'react';
import { Form, FormView } from '../..';
import { IPropertyField } from '../../../../entities/common';
import { IFormViewerProperties, IFormDocumentData } from './entities';
import DataBoundControl from '../../base/DataBoundControl';
import { EventEmitter, FormItemTypes, IDictionary } from '@bimser/core';
import { getControlData } from './helpers';
import { Map } from 'immutable';
import { IFormViewer } from '../FormView/entities';
import _isEqual from 'lodash/isEqual';

export default class FormViewer extends DataBoundControl {
	projectId: string;
	projectName: string;
	formName: string;
	formId: string;
	viewName: string;
	height: number;
	activeView: string;
	documentId: number;
	documentData: IFormDocumentData;

	__items: Map<string, LayoutCreatorEntities.LayoutDataItem> = Map();
	__getFormViewProps: () => IFormViewer = null;
	__changeProperty?: (data: FormsEntities.IPropertyChangeTransaction) => void;
	__formViewerForm: Form;
	__formViewerEventEmitter: EventEmitter;

	constructor() {
		super();

		this.__formViewerEventEmitter = new EventEmitter();
		this.controlType = FormItemTypes.EControlTypes.FormViewer;
		this.documentData = { documentId: 0, controls: [] };
		this.documentId = 0;
		this.activeView = '';
		this.projectId = '';
		this.projectName = '';
		this.formName = '';
		this.formId = '';
		this.viewName = '';
		this.height = 450;

		this.__setForm = this.__setForm.bind(this);
		this.__updateFormDocumentData = this.__updateFormDocumentData.bind(this);
		this.__beforeLoadProject = this.__beforeLoadProject.bind(this);
	}

	initialize() {
		super.initialize();
	}

	__loadProperties(data: IFormViewerProperties) {
		super.__loadProperties({ ...data, controlType: FormItemTypes.EControlTypes.FormViewer });

		this.__proxyChanged.projectId = data.projectId;
		this.__proxyChanged.formId = data.formId;
		this.__proxyChanged.height = data.height;
		this.__proxyChanged.activeView = data.activeView;
		this.__proxyChanged.documentId = data.documentId;
		this.__proxyChanged.documentData = data.documentData;

		this.__beforeLoadProject(data);

		if (this.__formViewerForm) {
			this.__formViewerForm.applyChanges().then(() => {
				this.__formViewerForm.__createControls();
			});
		}
	}

	__beforeLoadProject(data: IFormViewerProperties) {
		const isProjectChanged = this.projectName !== data.projectName;
		const isFormChanged = this.formName !== data.formName;
		const isViewChanged = this.viewName !== data.viewName;

		this.__proxyChanged.projectName = data.projectName;
		this.__proxyChanged.formName = data.formName;
		this.__proxyChanged.viewName = data.viewName;

		if (this.__isInitialized && (isProjectChanged || isFormChanged || isViewChanged)) {
			this.__form.applyChanges().then(() => {
				this.__form.__setFormViewerControls();
			});
		}
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			projectId: this.projectId,
			projectName: this.formName,
			formName: this.viewName,
			viewName: this.projectName,
			formId: this.formId,
			height: this.height,
			documentId: this.documentId,
			documentData: this.documentData
		};
	}

	__getPropertyFields(): IPropertyField[] {
		const fields = super.__getPropertyFields();
		return [
			...fields,
			{
				name: 'projectId',
				type: 'string'
			},
			{
				name: 'formName',
				type: 'string'
			},
			{
				name: 'viewName',
				type: 'string'
			},
			{
				name: 'projectName',
				type: 'string'
			},
			{
				name: 'formId',
				type: 'string'
			},
			{
				name: 'height',
				type: 'string'
			}
		];
	}

	__setForm(form: any) {
		this.__formViewerForm = form;
	}

	__updateFormDocumentData(items: Map<string, LayoutCreatorEntities.LayoutDataItem>) {
		this.__items = items;

		let controls = [...(this.documentData?.controls || [])];

		this.__items.forEach(item => {
			const { text, value } = getControlData(item);
			const controlName = item.getIn(['properties', 'name']);
			const isExistControl = controls.find(data => data.name === controlName);

			if (controlName && isExistControl) {
				controls = controls.map(data =>
					data.name === controlName ? { ...data, value, text } : data
				);
			} else if (controlName) {
				controls = [...controls, { name: controlName, text: text || '', value }];
			}
		});

		if (!_isEqual(controls, this.documentData?.controls)) {
			this.__proxyChanging.documentData = {
				documentId: this.documentData?.documentId || 0,
				controls
			};
		}
	}

	__render(children?: React.ReactNode) {
		const getFormView = () => {
			if (this.__getFormViewProps) {
				const props: IFormViewer = { ...this.__getFormViewProps() };
				this.__updateFormDocumentData(props.data);

				return (
					<FormView
						{...props}
						formViewerProps={{
							formViewerControlType: FormItemTypes.EControlTypes.FormViewer,
							formViewerControlName: this.name,
							formViewerFormId: this.__form.__getWindowId().split('_')[0],
							formViewerViewName: this.viewName
						}}
						windowId={`${props.windowId}_${this.documentId || 0}`}
						eventEmitter={this.__formViewerEventEmitter}
						documentId={this.documentId}
						setForm={this.__setForm}
						data={props.data}
						view={props.view}
						hasScrollbar
					/>
				);
			}

			return <div></div>;
		};

		return super.__render(
			<div
				style={{
					height: this.height,
					borderColor: '#bfbfbf',
					borderStyle: 'solid',
					borderWidth: '0.5px'
				}}
			>
				{getFormView()}
			</div>
		);
	}
}
